import { estateObjectAddressLine } from '@/utils/estate-object'

type EstateObjectData = App.Data.EstateObject.EstateObjectData
type EstateObjectType = App.Enums.EstateObject.EstateObjectType

export interface PortfolioRow {
  id: string
  addressLine: string
  zipCode: string
  city: string
  estateObjectType: EstateObjectType | null
  estateObjectTypeLabel: string
  owner: string
  units: number | null
  area: number | null
  price: number | null
  rent: number | null
  yield: number | null
  missingFields: number
  activeFinancingRequests: number
}

interface RowLabels {
  typeLabel: (type: EstateObjectType | null | undefined) => string
  withoutOwner: string
}

const toNumber = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value)

const grossYield = (rent: number | null, price: number | null): number | null =>
  rent !== null && price !== null && price > 0 ? ((rent * 12) / price) * 100 : null

export function toPortfolioRows(
  estateObjects: EstateObjectData[],
  missingFieldCounts: Record<string, number>,
  activeFinancingRequestCounts: Record<string, number>,
  labels: RowLabels,
): PortfolioRow[] {
  return estateObjects.map((estateObject) => {
    const price = toNumber(estateObject.purchase_price)
    const rent = toNumber(estateObject.actual_rent)
    const area = (estateObject.living_space_size ?? 0) + (estateObject.business_space_size ?? 0)

    return {
      id: estateObject.id ?? '',
      addressLine: estateObjectAddressLine(estateObject),
      zipCode: estateObject.zip_code ?? '',
      city: estateObject.city ?? '',
      estateObjectType: estateObject.estate_object_type ?? null,
      estateObjectTypeLabel: labels.typeLabel(estateObject.estate_object_type),
      owner: estateObject.owner?.name ?? labels.withoutOwner,
      units: estateObject.number_of_units ?? null,
      area: area > 0 ? area : null,
      price,
      rent,
      yield: grossYield(rent, price),
      missingFields: missingFieldCounts[estateObject.id ?? ''] ?? 0,
      activeFinancingRequests: activeFinancingRequestCounts[estateObject.id ?? ''] ?? 0,
    }
  })
}

export function sumBy(rows: PortfolioRow[], pick: (row: PortfolioRow) => number | null): number {
  return rows.reduce((total, row) => total + (pick(row) ?? 0), 0)
}

export function averageYield(rows: PortfolioRow[]): number | null {
  const yields = rows.map((row) => row.yield).filter((value): value is number => value !== null)

  return yields.length ? yields.reduce((total, value) => total + value, 0) / yields.length : null
}

export function groupRowsByOwner(rows: PortfolioRow[]): Map<string, PortfolioRow[]> {
  const grouped = new Map<string, PortfolioRow[]>()

  rows.forEach((row) => grouped.set(row.owner, [...(grouped.get(row.owner) ?? []), row]))

  return grouped
}

export function yieldClass(value: number | null): string {
  if (value === null) {
    return ''
  }

  return value >= 5 ? 'text-success-strong font-bold' : value < 3.5 ? 'text-warning font-bold' : ''
}
